const validator = require('ui-validator');
const { replaceAllString } = require('../../shared_modules/baui-utils');

const v = validator.validators;
const UiMoney = require('ui-money');
const trim = require('lodash.trim');
const isValid = require('date-fns/is_valid');
const isAfter = require('date-fns/is_after');
const differenceInYears = require('date-fns/difference_in_years');
const dynamicModuleRegistry = require('dynamic-module-registry');
const { SPECIAL_LIMITED_CHARACTERS_REGEX } = require('../../shared_modules/baui-constants');
const { toMoney } = require('../../shared_modules/offers/client/utils');
const {
  skipIfMissing,
  isTruthy,
  requiredExpense,
  homeStatusRequired,
} = require('../../shared_modules/baui-utils/client/validation');
const {
  SENTILINK_AGREEMENTS,
} = require('../../shared_modules/local-propulsion/components/SentilinkAgreement/constants');
const { default: loanProperties } = require('../../shared_modules/common-properties/loanProperties');

const borrowerStates = dynamicModuleRegistry.get('borrowerStates'); // getting pre-registered data from server

const specialCharacterValidation = message =>
  skipIfMissing({
    validateFn: v.customCondition({
      condition: SPECIAL_LIMITED_CHARACTERS_REGEX,
      message,
    }),
  });

function isJoint(attributes) {
  return attributes.hasOwnProperty('loanType') && attributes.loanType === 'joint';
}

const isNotLendingClubEmail = validator.createValidator((options, value) => {
  const opts = options || {};
  const val = value && value.trim();

  if (val && /^.+@lendingclub.com$/i.test(val))
    return opts.message || 'You are not able to use your LendingClub email. Please use another email address.';
});

var isValidDOB = validator.createValidator((options, value, key, attributes) => {
  // expects a date via keys dayBirth__ monthBirth__ yearBirth__
  options = options || {};

  var keyRegex = /^monthBirth(\w*)/;
  var match = key.match(keyRegex);
  var invalidMsg = 'A valid date of birth is required';
  if (match) {
    var suffix = match[1]; // empty str or 'Secondary' for joint || Single or SingleSecondary for singleDOB input for personal-loan
    var month = attributes['monthBirth' + suffix];
    var day = attributes['dayBirth' + suffix];
    var year = attributes['yearBirth' + suffix];

    if (month === '' || day === '' || year === '') return invalidMsg;
    if (year.length !== 4) return invalidMsg;

    var dob = new Date(year, parseInt(month, 10) - 1, day); // js Date month is zero-based
    if (dob.getMonth() !== parseInt(month, 10) - 1) return invalidMsg; // fix js Date auto correction
    if (!isValid(dob) || !isAfter(dob, new Date(1900, 0, 1))) return invalidMsg;

    var age = differenceInYears(new Date(), dob);
    if (age < 18) return 'You must be at least 18 years old to apply';

    return;
  }
  return invalidMsg; // return an err on ui if validator is used incorrectly--you shall not pass
  // TODO logger error if we use this on server too?
});

// custom validator that checks if the value is a valid ui-money value
var isUiMoney = validator.createValidator((options, value, key, attributes) => {
  if (!value) {
    return; // "required" validator will catch missing fields
  }
  var parsed = new UiMoney(value);

  if (isNaN(parsed.getValue())) {
    return 'Please enter a valid dollar amount';
  }
});

var isSigned = validator.createValidator((options, value, key, attributes) => {
  if (value !== 'on') {
    return 'You must electronically sign to accept the Terms of Use, the Credit Profile Authorization, and for LendingClub to verify your income.';
  }
});

var isValidState = validator.createValidator((options, value, key, attributes) => {
  // Validate only if filled & borrower-states array is present
  if (Array.isArray(borrowerStates) && attributes.hasOwnProperty(key) && value !== '') {
    var thisState = value.toUpperCase();
    var selection = borrowerStates.filter(state => {
      return state.stateCode === thisState;
    });
    if (!selection || !selection.length) {
      return 'A valid 2-letter state abbreviation is required';
    }
    var unsupportedStates = borrowerStates.filter(state => {
      return !state.eligible;
    });
    var unsupportedStatesCode = unsupportedStates.map(state => {
      return state.stateCode;
    });
    var unsupportedStatesName = unsupportedStates.map(state => {
      return state.stateName;
    });
    if (unsupportedStatesCode.indexOf(thisState) > -1) {
      return (
        'At this time, we are not accepting loan applications from the following states: ' +
        unsupportedStatesName.join(', ')
      );
    }
  }
});

const getDollarAmountRangeConfig = invalidMessage => ({
  min: 0,
  max: 9999999,
  underMinMessage: 'The income you have entered is negative',
  overMaxMessage: 'The income you have entered is above the maximum income allowed',
  invalidMessage,
});

module.exports = {
  code: true,
  leadType: true,
  prescreenEntryId: true,
  creditScore: true,
  loanAmountIsHidden: true,
  homeStatus: {
    required: (value, key, attrs) => {
      return homeStatusRequired(value, key, attrs);
    },
  },
  homeStatusSecondary: {
    required: (value, key, attrs) => {
      return homeStatusRequired(value, key, attrs);
    },
  },
  homeStatusExpense: {
    validateFn: requiredExpense,
  },
  homeStatusExpenseSecondary: {
    validateFn: requiredExpense,
  },
  loanType: {
    required: skipIfMissing({ validateFn: v.required({ key: 'application type' }) }),
    whitelist: v.whitelist({ whitelist: ['individual', 'joint', undefined] }),
    // TODO move skipIfMissing and dependent undefinedness in whitelist to ui-validator
    // will be undefined if skipped because missing
    // (ie when loan app/loanType is locked after signing til, loanType is disabled and thus missing)
  },
  firstName: {
    required: skipIfMissing({ validateFn: v.required({ message: 'Your first name is required' }) }),
    twoNames: skipIfMissing({
      validateFn: v.twoNames({
        message:
          "It appears that you have entered more than one individual's first name. Please apply using only one person's information.",
      }),
    }),
    inputLength: skipIfMissing({
      validateFn: v.inputLength({ max: 40, message: 'The length of the first name must be less than 40 characters' }),
    }),
    suspicious: v.suspicious(),
  },
  lastName: {
    required: skipIfMissing({ validateFn: v.required({ message: 'Your last name is required' }) }),
    inputLength: skipIfMissing({ validateFn: v.inputLength({ max: 40, key: 'Your last name' }) }),
    suspicious: v.suspicious(),
  },
  streetAddress: {
    required: skipIfMissing({ validateFn: v.required({ key: 'street address' }) }),
    inputLength: skipIfMissing({ validateFn: v.inputLength({ max: 50, key: 'street address' }) }),
    suspicious: v.suspicious(),
  },
  city: {
    required: skipIfMissing({ validateFn: v.required() }),
    inputLength: skipIfMissing({ validateFn: v.inputLength({ max: 40 }) }),
    suspicious: v.suspicious(),
    isAlpha: skipIfMissing({
      // need skipIfMissing for pi locked??
      validateFn: v.customCondition({ condition: /[0-9]/, message: 'City may only contain letters' }),
    }),
    noSpecialChars: specialCharacterValidation('Use a valid name for city'),
    customCondition: skipIfMissing({
      validateFn: v.customCondition({
        message:
          "Please revise your city to ensure that it doesn't include your name, Social Security Number, email address, or other information that could identify where you live. Please do not use any of the following characters: *)",
        condition: /.*(@|(\.com|\.net|\.edu|\.gov|\.org|\.biz)(?![a-z])|\*).*/,
      }),
    }),
  },
  state: {
    required: skipIfMissing({ validateFn: v.required() }),
    suspicious: v.suspicious(),
    checkValidState: skipIfMissing({ validateFn: isValidState() }),
    noSpecialChars: specialCharacterValidation('Use a valid name for state'),
  },
  zip: {
    required: skipIfMissing({ validateFn: v.required({ key: 'Zip Code' }) }),
    validZipcode: skipIfMissing({ validateFn: v.validZipcode() }),
    suspicious: v.suspicious(),
  },
  primaryPhone: {
    required: skipIfMissing({ validateFn: v.required({ message: 'Enter a valid phone number' }) }),
    validTelephone: v.validTelephone({ message: 'Enter a valid phone number' }),
    suspicious: v.suspicious(),
  },
  phone: {},
  phoneType: {},
  phoneSource: {},
  // if loan amount hidden don't do client-side validation and set server catch error (when non-DM flow can't edit loan amount)
  loanAmount: {
    required: skipIfMissing({ validateFn: v.required({ key: 'loan amount' }) }),
    dollarAmountRange: skipIfMissing({
      validateFn: (value, key, attributes) => {
        return v.dollarAmountRange({
          min: 1000,
          max: loanProperties.MAXIMUM_AMOUNT_TO_BORROW,
          message: `Please enter an amount between $1000 and $${loanProperties.MAXIMUM_AMOUNT_TO_BORROW}`,
        })(value, key, attributes);
      },
    }),
    suspicious: v.suspicious(),
    isUiMoney: skipIfMissing({ validateFn: isUiMoney() }),
  },
  additionalCash: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && !attributes.invalidRange) {
        return v.required({
          message: `Please enter an additional amount between ${toMoney(attributes.minCashAmount, {
            precision: 0,
          })} and ${toMoney(attributes.maxCashAmount, { precision: 0 })}`,
        })(value, key, attributes);
      }
    },
    dollarAmountRange: skipIfMissing({
      validateFn: (value, key, attributes) => {
        if (!attributes.invalidRange) {
          return v.dollarAmountRange({
            min: attributes?.minCashAmount,
            max: attributes?.maxCashAmount,
            message:
              attributes.minCashAmount &&
              attributes.maxCashAmount &&
              `Please enter an additional amount between ${toMoney(attributes.minCashAmount, {
                precision: 0,
              })} and ${toMoney(attributes.maxCashAmount, { precision: 0 })}`,
          })(value, key, attributes);
        }
      },
    }),
    suspicious: v.suspicious(),
    isUiMoney: skipIfMissing({ validateFn: isUiMoney() }),
  },
  loanPurpose: {
    // required: v.required({message: 'A loan purpose is required'})
    required: skipIfMissing({ validateFn: v.required({ key: 'loan purpose' }) }),
  },
  monthlyPayment: {
    isUiMoney: skipIfMissing({ validateFn: isUiMoney() }),
  },
  monthBirth: {
    validDate: skipIfMissing({ validateFn: isValidDOB() }),
  },
  dayBirth: {},
  yearBirth: {},
  monthBirthSingle: {
    validDate: skipIfMissing({ validateFn: isValidDOB() }),
  },
  dayBirthSingle: {},
  yearBirthSingle: {},
  email: {
    required: skipIfMissing({ validateFn: v.required({ message: 'The email address is required' }) }),
    email: v.email(),
    notLCEmail: isNotLendingClubEmail(),
    inputLength: skipIfMissing({ validateFn: v.inputLength({ max: 50, key: 'Your email address' }) }),
  },
  password: {
    required: skipIfMissing({ validateFn: v.required({ message: 'A password is required' }) }),
    validPassword: skipIfMissing({ validateFn: v.validPassword() }),
    inputLength: skipIfMissing({ validateFn: v.inputLength({ min: 8, max: 40, key: 'Your password' }) }),
    notSameAsField: skipIfMissing({
      validateFn: v.notSameAsField({
        key: 'Your password',
        target: 'email',
        message: 'Your password must not match your login email',
      }),
    }),
    suspicious: v.suspicious(),
  },
  income: {
    required: skipIfMissing({ validateFn: v.required({ message: 'The income is required' }) }),
    dollarAmountRange: skipIfMissing({
      validateFn: v.dollarAmountRange(getDollarAmountRangeConfig('The income you have entered is not valid')),
    }),
    isUiMoney: skipIfMissing({ validateFn: isUiMoney() }),
  },
  incomeAdditional: {
    // note additionalIncome is always optional, not just skip for pi1 locked
    dollarAmountRange: skipIfMissing({
      validateFn: v.dollarAmountRange(getDollarAmountRangeConfig('Enter a valid income')),
    }),
  },
  ssn: {
    required: skipIfMissing({ validateFn: v.required({ message: 'Your Social Security number is required' }) }),
    validSSN: skipIfMissing({
      validateFn: (value, key, attributes) => {
        if (attributes.hasOwnProperty(key)) {
          return v.validSSN()(replaceAllString(value), key, attributes);
        }
      },
    }),
    suspicious: v.suspicious(),
  },
  sentilinkConsent: {
    required: skipIfMissing({ validateFn: isTruthy({ message: SENTILINK_AGREEMENTS.validationErrorText }) }),
  },
  experiments: {
    required: skipIfMissing({ validateFn: v.required({ message: 'Experiments is required' }) }),
  },
  firstNameSecondary: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.required({ message: 'The first name is required' })(value, key, attributes);
      }
    },
    twoNames: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.twoNames({
          message:
            "It appears that you have entered more than one individual's first name. Please enter one person's information.",
        })(value, key, attributes);
      }
    },
    isAlpha: v.customCondition({ condition: /[0-9]/, message: 'The first name may only contain letters' }),
    inputLength: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.inputLength({ max: 40, message: 'The length of the first name must be less than 40 characters' })(
          value,
          key,
          attributes
        );
      }
    },
    suspicious: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.suspicious()(value, key, attributes);
      }
    },
  },
  lastNameSecondary: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.required({ message: 'The last name is required' })(value, key, attributes);
      }
    },
    isAlpha: v.customCondition({ condition: /[0-9]/, message: 'The last name may only contain letters' }),
    inputLength: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.inputLength({ max: 40, key: 'The last name' })(value, key, attributes);
      }
    },
    suspicious: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.suspicious()(value, key, attributes);
      }
    },
  },
  monthBirthSecondary: {
    validDate: skipIfMissing({
      validateFn: (value, key, attributes) => {
        if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
          return isValidDOB()(value, key, attributes);
        }
      },
    }),
  },
  dayBirthSecondary: {},
  yearBirthSecondary: {},
  monthBirthSingleSecondary: {
    validDate: skipIfMissing({
      validateFn: (value, key, attributes) => {
        if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
          return isValidDOB()(value, key, attributes);
        }
      },
    }),
  },
  dayBirthSingleSecondary: {},
  yearBirthSingleSecondary: {},
  emailSecondary: {
    required: skipIfMissing({
      validateFn: (value, key, attributes) => {
        if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
          return v.required({ message: 'The email address is required' })(value, key, attributes);
        }
      },
    }),
    email: skipIfMissing({
      validateFn: (value, key, attributes) => {
        if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
          return v.email()(value, key, attributes);
        }
      },
    }),
    notLCEmail: isNotLendingClubEmail(), // TODO check isJoint etc?
    inputLength: skipIfMissing({
      validateFn: (value, key, attributes) => {
        if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
          return v.inputLength({ max: 50, key: 'Your email address' })(value, key, attributes);
        }
      },
    }),
    isSameAsPrimaryEmail: skipIfMissing({
      validateFn: (value, key, attributes) => {
        if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
          const primaryEmail = attributes.primaryEmail;
          if (primaryEmail !== '' && value === primaryEmail) {
            return `${
              attributes.firstName && attributes.firstName !== '' ? attributes.firstName : 'Your co-borrower'
            }’s email cannot match your email`;
          }
        }
      },
    }),
  },
  incomeSecondary: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.required({ message: 'The income is required' })(value, key, attributes);
      }
    },
    dollarAmountRange: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.dollarAmountRange(getDollarAmountRangeConfig('The income you have entered is not valid'))(
          value,
          key,
          attributes
        );
      }
    },
    isUiMoney: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return isUiMoney()(value, key, attributes);
      }
    },
  },
  incomeNonTaxableSecondary: {
    dollarAmountRange: skipIfMissing({
      validateFn: v.dollarAmountRange({
        min: 0,
        max: 9999999,
        underMinMessage: 'The non-taxable income you have entered is negative',
        overMaxMessage: 'The non-taxable income you have entered is above the maximum income allowed',
        invalidMessage: 'The non-taxable income you have entered is not valid',
      }),
    }),
    isUiMoney: skipIfMissing({ validateFn: isUiMoney() }),
  },
  ssnSecondary: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.required({ message: 'The Social Security number is required' })(value, key, attributes);
      }
    },
    validSSN: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.validSSN()(replaceAllString(value), key, attributes);
      }
    },
    suspicious: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.suspicious()(value, key, attributes);
      }
    },
    isNotSameAsPrimarySSN: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.notSameAsField({
          target: 'ssn',
          message: 'Social Security Numbers for 1st and 2nd Co-Borrowers cannot be the same',
        })(value, key, attributes);
      }
    },
  },
  streetAddressSecondary: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.required({ key: 'street address' })(value, key, attributes);
      }
    },
    inputLength: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.inputLength({ max: 50, key: 'Your street address' })(value, key, attributes);
      }
    },
    suspicious: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.suspicious()(value, key, attributes);
      }
    },
  },
  citySecondary: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.required()(value, key, attributes);
      }
    },
    inputLength: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.inputLength({ max: 40 })(value, key, attributes);
      }
    },
    suspicious: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.suspicious()(value, key, attributes);
      }
    },
    isAlpha: skipIfMissing({
      // need skipIfMissing for pi locked??
      validateFn: v.customCondition({ condition: /[0-9]/, message: 'City may only contain letters' }),
    }),
    noSpecialChars: specialCharacterValidation('Use a valid name for city'),
    customCondition: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.customCondition({
          message:
            "Please revise your city to ensure that it doesn't include your name, Social Security Number, email address, or other information that could identify where you live. Please do not use any of the following characters: *)",
          condition: /.*(@|(\.com|\.net|\.edu|\.gov|\.org|\.biz)(?![a-z])|\*).*/,
        })(value, key, attributes);
      }
    },
  },
  stateSecondary: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.required({ key: 'state' })(value, key, attributes);
      }
    },
    suspicious: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.suspicious()(value, key, attributes);
      }
    },
    checkValidState: (value, key, attributes) => {
      if (isJoint(attributes)) {
        return isValidState()(value, key, attributes);
      }
    },
    noSpecialChars: specialCharacterValidation('Use a valid name for state'),
  },
  zipSecondary: {
    required: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.required({ key: 'Zip Code' })(value, key, attributes);
      }
    },
    validZipcode: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.validZipcode()(value, key, attributes);
      }
    },
    suspicious: (value, key, attributes) => {
      if (attributes.hasOwnProperty(key) && isJoint(attributes)) {
        return v.suspicious()(value, key, attributes);
      }
    },
  },
  agreements: {
    required: v.required({
      message:
        'You must electronically sign to accept the Terms of Use, the Credit Profile Authorization, and for LendingClub to verify your income.',
    }),
    isSigned: skipIfMissing({ validateFn: isSigned() }),
  },
  originator: {
    required: skipIfMissing({ validateFn: v.required({ message: 'An originator is required' }) }),
  },

  //  auto and personal agreements version hidden input
  PrivacyPolicy: true,
  SharedTermsOfUse: true,
  PersonalCreditProfileAuth: true,
  SharedElectronicConsent: true,
  TermsOfUse: true,
  ApplicationTermsOfUse: true,
  CreditProfileAuthorization: true,
  hasLoanAppInProgress: true,
  loanIntention: true,
  isMFAReturn: true,
  offerEligibilityId: true,
  dpIntentResponseFlg: true,
  dpExpectedTransferAmt: true,
  prescreenPayLoad: true,
  showRentField: true,
};

